import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Lock, Building2 } from "lucide-react";

export default function LoginRegister() {
  const [tab, setTab] = useState("login");
  const [submitted, setSubmitted] = useState(false);

  return (
    <div className="bg-leduro-surface py-20 md:py-28" data-testid="login-page">
      <div className="leduro-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {/* Left: marketing */}
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <span className="leduro-eyebrow">— Trade Portal</span>
          <h1 className="mt-3 font-heading font-black tracking-tighter text-leduro-navy text-3xl md:text-5xl text-balance">
            Log in or open a B2B account.
          </h1>
          <p className="mt-5 text-leduro-muted leading-relaxed">
            Verified trade buyers see net pricing, project tiers, dedicated engineer contacts and a unified order
            history across all Baltic warehouses.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "Net trade pricing on every active SKU",
              "Full IES / LDT photometric library",
              "One-click reorder from past invoices",
              "Project quote builder with PDF export",
              "Same-day technical engineer support",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="h-5 w-5 grid place-items-center bg-leduro-orange text-white shrink-0">
                  <Check className="h-3 w-3" />
                </span>
                <span className="text-sm text-leduro-navy">{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 border border-leduro-border bg-white p-5 flex items-center gap-4">
            <Lock className="h-6 w-6 text-leduro-orange shrink-0" />
            <p className="text-xs text-leduro-muted leading-relaxed">
              Leduro is a B2B-only platform. Private accounts are not approved. Registration requires a valid company
              registration number and VAT ID.
            </p>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <div className="bg-white border border-leduro-border">
            <div className="grid grid-cols-2">
              <TabBtn active={tab === "login"} onClick={() => setTab("login")} testid="tab-login">
                Log In
              </TabBtn>
              <TabBtn active={tab === "register"} onClick={() => setTab("register")} testid="tab-register">
                Register
              </TabBtn>
            </div>

            {submitted ? (
              <div className="p-10 text-center" data-testid="auth-success">
                <div className="h-14 w-14 grid place-items-center bg-leduro-orange text-white mx-auto">
                  <Check className="h-7 w-7" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-leduro-navy text-2xl">
                  {tab === "login" ? "Welcome back." : "Application received."}
                </h3>
                <p className="mt-3 text-sm text-leduro-muted max-w-md mx-auto">
                  {tab === "login"
                    ? "This is a prototype — in production, you'd be redirected to your trade dashboard."
                    : "We typically approve trade accounts within one business day after VAT verification."}
                </p>
                <Link to="/" className="leduro-btn-ghost mt-8 inline-flex" data-testid="auth-success-home">
                  Back to home
                </Link>
              </div>
            ) : (
              <form
                className="p-8 space-y-5"
                onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }}
                data-testid={`form-${tab}`}
              >
                {tab === "register" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="First name" testid="reg-first" required />
                      <Field label="Last name" testid="reg-last" required />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Company name" testid="reg-company" required icon={Building2} />
                      <Field label="VAT ID" testid="reg-vat" required />
                    </div>
                    <Field label="Country" testid="reg-country" required />
                  </>
                )}

                <Field label="Email" type="email" testid={`${tab}-email`} required />
                <Field label="Password" type="password" testid={`${tab}-password`} required />

                {tab === "login" ? (
                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center gap-2 text-leduro-navy">
                      <input type="checkbox" data-testid="remember-me" className="accent-leduro-orange" /> Remember me
                    </label>
                    <a href="#" className="text-leduro-orange font-semibold hover:underline" data-testid="forgot-link">
                      Forgot password?
                    </a>
                  </div>
                ) : (
                  <label className="flex items-start gap-2 text-xs text-leduro-muted">
                    <input type="checkbox" required data-testid="reg-terms" className="mt-1 accent-leduro-orange" />
                    I confirm I am acting on behalf of a company and agree to Leduro's Terms of Trade.
                  </label>
                )}

                <button type="submit" data-testid={`submit-${tab}`} className="leduro-btn-primary w-full justify-center">
                  {tab === "login" ? "Log In" : "Apply for Trade Account"}
                  <ArrowUpRight className="h-4 w-4" />
                </button>

                <p className="text-center text-xs text-leduro-muted pt-2">
                  {tab === "login" ? "Don't have an account?" : "Already a partner?"}{" "}
                  <button
                    type="button"
                    className="text-leduro-orange font-semibold"
                    onClick={() => setTab(tab === "login" ? "register" : "login")}
                    data-testid="switch-tab-link"
                  >
                    {tab === "login" ? "Apply here" : "Log in"}
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const TabBtn = ({ active, children, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`py-4 text-sm font-bold uppercase tracking-wider transition-colors ${
      active ? "bg-leduro-navy text-white" : "bg-white text-leduro-navy hover:text-leduro-orange"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, type = "text", testid, required, icon: Icon }) => (
  <div>
    <label className="text-xs uppercase tracking-wider font-bold text-leduro-navy">
      {label} {required && <span className="text-leduro-orange">*</span>}
    </label>
    <div className="relative mt-1.5">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-leduro-muted" />}
      <input
        type={type}
        required={required}
        data-testid={testid}
        className={`w-full border border-leduro-border ${Icon ? "pl-10" : "px-3"} pr-3 py-2.5 text-sm focus:outline-none focus:border-leduro-orange`}
      />
    </div>
  </div>
);
